import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { Tabs } from '@/components';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import type { ActivityPulseWindow } from '@/types/admin-settings';
import { useActivityPulse } from '../hooks/useActivityPulse';
import { useActivityTrends } from '../hooks/useActivityTrends';
import { buildMetrics } from './utils';
import { DeltaIndicator } from './DeltaIndicator';
import { TrendChartCard } from './TrendChartCard';

export function ActivityPulseSection() {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const [windowDays, setWindowDays] = useState<ActivityPulseWindow>(7);
    const { pulse, isLoading, error } = useActivityPulse(windowDays);
    const { trends, error: trendsError } = useActivityTrends(windowDays);

    const tabs = [
        { id: '7', label: t('organization.activityPulse.window7d') },
        { id: '30', label: t('organization.activityPulse.window30d') },
    ];

    const previousLabel = t('organization.activityPulse.vsPrior', {
        days: windowDays,
    });

    const chartInterval = windowDays > 7 ? 4 : 'preserveStartEnd';

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-white-100">
                    {t('organization.activityPulse.title')}
                </h3>
                <Tabs
                    tabs={tabs}
                    activeTab={String(windowDays)}
                    onTabChange={(id) =>
                        setWindowDays(Number(id) as ActivityPulseWindow)
                    }
                />
            </div>

            {error ? (
                <div className="border border-danger-300 rounded-[5px] p-4 text-danger-200 text-sm">
                    {t('organization.activityPulse.loadFailed')}
                </div>
            ) : (
                <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
                    {(pulse ? buildMetrics(pulse, t) : []).map((item) => (
                        <div
                            key={item.label}
                            className="bg-transparent border border-grey-600 rounded-[10px] p-4 flex flex-col gap-3"
                        >
                            <div className="flex items-center gap-1">
                                <item.icon
                                    className="size-[14px] text-grey-200"
                                    weight="regular"
                                />
                                <span className="text-sm text-grey-200">
                                    {item.label}
                                </span>
                            </div>
                            <span className="text-lg text-white leading-7">
                                {isLoading ? (
                                    '—'
                                ) : (
                                    <AnimatedNumber
                                        value={item.metric.value}
                                        willChange
                                    />
                                )}
                            </span>
                            {!isLoading && (
                                <DeltaIndicator
                                    deltaPercent={item.metric.deltaPercent}
                                    previousLabel={previousLabel}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}

            {!trendsError && trends && (
                <div className="grid gap-3 grid-cols-1 @medium:grid-cols-2">
                    <TrendChartCard
                        title={t('organization.activityPulse.activeUsersTrend')}
                        data={trends.activeUsers}
                        tooltipLabel={t(
                            'organization.activityPulse.activeUsers',
                        )}
                        strokeColor="white"
                        fillColor="white"
                        interval={chartInterval}
                    />
                    <TrendChartCard
                        title={t('organization.activityPulse.alertVolumeTrend')}
                        data={trends.alertVolume}
                        tooltipLabel={t(
                            'organization.activityPulse.alertVolume',
                        )}
                        strokeColor="white"
                        fillColor="white"
                        interval={chartInterval}
                    />
                </div>
            )}
        </div>
    );
}
